import { useEffect, useRef, useState } from "react";
import { useExchangeCodeMutation, useRefreshMutation } from "@/src/auth/services/auth-http.service";
import { getIsAuthenticated, getRefreshToken } from "@/src/auth/store/auth-selectors";
import { setImpersonatorId, setRefreshToken, setUser } from "@/src/auth/store/auth-slice";
import { LOGOUT_BROADCAST_KEY, logOutAndReset } from "@/src/auth/store/logout-thunk";
import { Spinner } from "@/src/common/components/ui";
import { getSubdomain } from "@/src/common/lib/subdomain";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { baseApi } from "@/src/common/services/base-api";
import { getAccessToken, setAccessToken } from "@/src/common/services/http.service";
import { setCommunities } from "@/src/community/store/community-slice";

function getAuthCodeFromUrl(): string | null {
	const params = new URLSearchParams(window.location.search);
	return params.get("auth_code");
}

function cleanAuthCodeFromUrl() {
	const url = new URL(window.location.href);
	url.searchParams.delete("auth_code");
	window.history.replaceState({}, "", url.toString());
}

export function AuthInitializer({ children }: { children: React.ReactNode }) {
	const dispatch = useAppDispatch();
	const isAuthenticated = useAppSelector(getIsAuthenticated);
	const storedRefreshToken = useAppSelector(getRefreshToken);
	const [refresh] = useRefreshMutation();
	const [exchangeCode] = useExchangeCodeMutation();
	const attempted = useRef(false);
	const subdomain = getSubdomain();
	const authCodeFromUrl = getAuthCodeFromUrl();
	const needsRehydration = !!authCodeFromUrl || (!!subdomain && !isAuthenticated) || (isAuthenticated && !getAccessToken());
	const [isHydrating, setIsHydrating] = useState(needsRehydration);

	useEffect(() => {
		if (attempted.current) return;
		if (!needsRehydration) return;
		attempted.current = true;

		const isCookieOnlyAttempt = !authCodeFromUrl && !storedRefreshToken;

		const doRefresh = async () => {
			let tokenForRefresh = storedRefreshToken ?? undefined;

			if (authCodeFromUrl) {
				const { refresh_token } = await exchangeCode({ code: authCodeFromUrl }).unwrap();
				tokenForRefresh = refresh_token;
				cleanAuthCodeFromUrl();
				dispatch(baseApi.util.resetApiState());
			}

			const refreshArg = tokenForRefresh ? { refresh_token: tokenForRefresh } : {};
			const result = await refresh(refreshArg).unwrap();

			setAccessToken(result.access_token);
			dispatch(setRefreshToken(result.refresh_token));
			dispatch(setUser(result.user));
			dispatch(setCommunities(result.communities));
			dispatch(setImpersonatorId(result.impersonator_id ?? null));
		};

		doRefresh()
			.catch(() => {
				if (authCodeFromUrl) {
					cleanAuthCodeFromUrl();
				}
				if (isCookieOnlyAttempt) {
					return;
				}
				dispatch(logOutAndReset({ skipRedirect: true }));
			})
			.finally(() => {
				setIsHydrating(false);
			});
	}, [dispatch, needsRehydration, refresh, exchangeCode, authCodeFromUrl, storedRefreshToken]);

	useEffect(() => {
		const handler = (event: StorageEvent) => {
			if (event.key !== LOGOUT_BROADCAST_KEY) return;
			if (!event.newValue) return;
			dispatch(logOutAndReset({ silent: true }));
		};
		window.addEventListener("storage", handler);
		return () => {
			window.removeEventListener("storage", handler);
		};
	}, [dispatch]);

	if (isHydrating) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<Spinner size="lg" />
			</div>
		);
	}

	return <>{children}</>;
}
